
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { memo } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Shirt } from "lucide-react";
import { formatTShirtSize as formatTShirt } from '@/utils/formatUtils';

// Memoized formatting functions for better performance
export const formatTimeAgo = (date: string) => {
  return formatDistanceToNow(new Date(date), { 
    addSuffix: true,
    locale: vi 
  });
};

export const formatDateTime = (date: string) => {
  return new Date(date).toLocaleString('vi-VN');
};

export const formatContribution = (amount: number) => {
  return new Intl.NumberFormat('vi-VN', { 
    style: 'currency', 
    currency: 'VND' 
  }).format(amount);
};

// Format T-shirt size with consistent display format and color coding
export const formatTShirtSize = (size?: string | null): string => {
  // Use the imported utility function
  return formatTShirt(size);
};

// Helper component for formatted dates with memoization
export const FormattedDate = memo(({ date, formatter }: { date: string, formatter: (date: string) => string }) => {
  return <span>{formatter(date)}</span>;
});

FormattedDate.displayName = 'FormattedDate';

// T-shirt size display component with tooltip
export const TShirtSizeDisplay = memo(({ size }: { size?: string | null }) => {
  // Add debug logging
  console.log("TShirtSizeDisplay received size:", size);
  
  const formattedSize = formatTShirtSize(size);
  console.log("TShirtSizeDisplay formatted size:", formattedSize);
  
  const sizeClass = formattedSize.includes('(Nam)') 
    ? 'text-blue-600 dark:text-blue-400 font-medium' 
    : formattedSize.includes('(Nữ)') 
      ? 'text-pink-600 dark:text-pink-400 font-medium'
      : 'text-gray-600 dark:text-gray-400';
  
  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <div className={`flex items-center space-x-1.5 cursor-help ${sizeClass}`}>
          <Shirt size={16} className="opacity-80" />
          <span>{formattedSize}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2">
        <p className="text-xs">Size áo: {formattedSize}</p>
        {size?.startsWith('Nam_') ? (
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">Áo thun nam</p>
        ) : size?.startsWith('Nu_') ? (
          <p className="text-xs text-pink-600 dark:text-pink-400 mt-1">Áo thun nữ</p>
        ) : null}
        {size && (
          <p className="text-xs text-gray-500 mt-1">Giá trị gốc: {size}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
});

TShirtSizeDisplay.displayName = 'TShirtSizeDisplay';
